import { useEffect, createContext, useContext, useMemo } from 'react';
import {
    LoginAndResetPassErrorCode,
    SignUpRequest,
    SignUpResponse,
} from '@/bo-kit/transport/types';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { SerializedError } from '@reduxjs/toolkit';
import { useNavigate } from 'react-router-dom';
import { VerifyCodeState } from '@/bo-kit/components/VerifyAccountPage/types';
import { useLoginErrorCode } from '@/bo-kit/hooks/useLoginErrorCode';
import { useReturnUrl } from '@/bo-kit/hooks/useReturnUrl';

type UseDoSignUpMutation = [
    (data: SignUpRequest) => void,
    {
        isLoading: boolean;
        isError: boolean;
        error?: FetchBaseQueryError | SerializedError;
        originalArgs?: SignUpRequest;
        data?: SignUpResponse;
        reset: () => void;
    }
];

export const UseDoSignUpMutationContext = createContext<UseDoSignUpMutation | undefined>(undefined);

const useDoSignUpMutationFromContext = () => {
    const contextValue = useContext(UseDoSignUpMutationContext);
    if (!contextValue) {
        throw new Error(
            "No useDoSignUpMutation defined with react context, pls implement: '<UseDoSignUpMutationContext.Provider value={[doSignUp, doSignUpSate]}>{children}</UseDoSignUpMutationContext.Provider>'"
        );
    }
    return contextValue;
};

export const useSignUp = () => {
    const navigate = useNavigate();

    const [doSignUp, state] = useDoSignUpMutationFromContext();

    const email = useMemo(() => state.originalArgs?.username, [state.originalArgs?.username]);

    const [returnUrl] = useReturnUrl();
    useEffect(() => {
        if (state.data) {
            navigate('../verify', {
                state: { id: state.data.id, email, returnUrl } as VerifyCodeState,
            });
        }
    }, [email, navigate, returnUrl, state.data]);
    const { errorCode } = useLoginErrorCode(state);
    const isUserExistError = useMemo(
        () => errorCode === LoginAndResetPassErrorCode.USEREXISTS,
        [errorCode]
    );
    const errorMessage = useMemo(() => {
        if (!state.isError) {
            return undefined;
        }
        if (!state.error || !('error' in state.error)) {
            return 'An unexpected error occurred';
        }
        return state.error.error;
    }, [state.error, state.isError]);

    return {
        doSignUp,
        isLoading: state.isLoading,
        isError: state.isError,
        isUserExistError,
        clearState: state.reset,
        errorMessage,
    };
};
